import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';

import { DEFAULT_LOCALE, isLocale, LOCALE_DIRECTION, type Locale } from './locale';
import { scopeTranslator, translatorFor, type Translate } from './translator';

interface LocaleValue {
  readonly locale: Locale;
  readonly t: Translate;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleValue | null>(null);

/** Remembered across reloads. A preference, not a credential — see `session.ts`. */
const STORAGE_KEY = 'kadir.console.locale';

function initialLocale(): Locale {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  // Arabic is the product language (ADR-013), so it is the default rather than
  // the fallback — an English browser in Riyadh still gets the console its
  // users read.
  const configured = import.meta.env.VITE_DEFAULT_LOCALE;
  return isLocale(configured) ? configured : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  // The document's own attributes, not a wrapper `<div dir>`. Direction has to
  // reach things React does not render — the scrollbar, native form controls,
  // the text selection handles on a phone — and only `<html>` carries it there.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = LOCALE_DIRECTION[locale];
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    globalThis.localStorage?.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo<LocaleValue>(
    () => ({ locale, t: translatorFor(locale), setLocale }),
    [locale, setLocale],
  );

  // Radix portals (menus, dialogs) render outside the tree and read direction from
  // its own context, not from `<html dir>` — without this every dropdown lays out
  // left-to-right in Arabic.
  return (
    <LocaleContext.Provider value={value}>
      <DirectionProvider dir={LOCALE_DIRECTION[locale]}>{children}</DirectionProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (value === null) {
    throw new Error('useLocale must be used inside <LocaleProvider>.');
  }
  return value;
}

/** `const t = useTranslate();` — the whole catalogue, keys fully qualified. */
export function useTranslate(): Translate {
  return useLocale().t;
}

/**
 * A translator scoped to one namespace: `useTranslations('admin.nav')` then
 * `t('talents')`.
 *
 * The same signature `next-intl` had, kept on purpose. Every component in the
 * console already calls it this way, and the alternative was rewriting a hundred
 * call sites from `t('talents')` to `t('admin.nav.talents')` — churn that would
 * have buried the parts of this conversion worth reading, and made the sidebar
 * noisier than it was under Next.
 */
export function useTranslations(namespace: string): Translate {
  const t = useLocale().t;

  return useMemo(() => scopeTranslator(t, namespace), [t, namespace]);
}
